import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import LayersIcon from '@mui/icons-material/Layers';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import IconButton from '@mui/material/IconButton';
import MapIcon from '@mui/icons-material/Map';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import {
  ConvectionTooltip, IceTooltip, TurbulenceTooltip, DustTooltip, formatCoordinates
} from '../common/MapTooltip';
import { buttonLabels } from '../../config/layerConfig';
import VisibilityGraph from './visibility-layer/visibility_graph';
import { MapNotice } from '../common/MapLoadingOverlay';
import { prefetchPmtiles } from '../../utils/pmtilesProtocol';
import { whenSourcesLoaded } from '../../utils/whenSourcesLoaded';
import axios from 'axios';

import Badge from '@mui/material/Badge';


// --- 🛰️ FRAMES RASTER COMO EL RADAR DE WINDY (heatmaps pmtiles y nowcasting WMS) ---
// Cada hora es su propia fuente raster, creada invisible. La hora elegida se enseña cuando ya tiene sus teselas (o a los
// 3 s, como el forceSwitch de Windy) con un fundido desde la anterior: entre horas el mapa nunca se queda en blanco y el
// play no espera a la red, porque la anterior y las FRAMES_AHEAD siguientes ya se están cargando.
// ponytail: tope de FRAMES_MAX fuentes; fuera van las más antiguas que no están en la ventana ni a la vista
const FRAMES_AHEAD = 3;
const FRAMES_MAX = 12;
const FRAME_FADE_MS = 300;
// Las horas van debajo de los polígonos WFS (si ya están en el mapa)
const POLYGON_LAYER_IDS = ['cnn-fill', 'ice-fill', 'turbulence-fill', 'dust-fill', 'visibility-circle'];
// Sin polígonos a esa hora: siempre el mismo objeto, así react-map-gl no llama a setData en cada render
const EMPTY_FC = { type: 'FeatureCollection', features: [] };
const frameSourceId = (id) => `frame-src-${id}`;
const frameLayerId = (id) => `frame-lyr-${id}`;

const useRasterFrames = (mapInstance, { frames, current, opacity, resampling }) => {
  const created = useRef(new Set()); // ids con fuente y capa en el mapa, en orden de creación
  const shown = useRef(null);
  const target = useRef(null); // la hora que marca el slider ahora mismo

  // Otra secuencia (capa, run, Retry o HEATMAPS apagado): fuera todas las fuentes de la anterior
  useEffect(() => () => {
    const map = mapInstance?.getMap ? mapInstance.getMap() : mapInstance;
    if (map?.style) {
      created.current.forEach((id) => {
        if (map.getLayer(frameLayerId(id))) map.removeLayer(frameLayerId(id));
        if (map.getSource(frameSourceId(id))) map.removeSource(frameSourceId(id));
      });
    }
    created.current.clear();
    shown.current = null;
  }, [mapInstance, frames]);

  useEffect(() => {
    const map = mapInstance?.getMap ? mapInstance.getMap() : mapInstance;
    if (!map?.style || !frames) return undefined;
    const index = frames.findIndex((f) => f.id === current);

    const ensure = (frame) => {
      if (created.current.has(frame.id)) return;
      map.addSource(frameSourceId(frame.id), frame.source);
      map.addLayer({
        id: frameLayerId(frame.id), type: 'raster', source: frameSourceId(frame.id),
        paint: {
          'raster-opacity': 0,
          'raster-opacity-transition': { duration: FRAME_FADE_MS, delay: 0 },
          'raster-fade-duration': 0,
          'raster-resampling': resampling,
        },
      }, POLYGON_LAYER_IDS.find((id) => map.getLayer(id)));
      created.current.add(frame.id);
    };
    const show = (id) => {
      created.current.forEach((f) => {
        if (map.getLayer(frameLayerId(f))) map.setPaintProperty(frameLayerId(f), 'raster-opacity', f === id ? opacity : 0);
      });
      shown.current = id;
    };

    target.current = current;
    const run = () => {
      try {
        if (!map.style) return;
        // Hora sin frame (fuera del run o todavía la de la capa anterior): no se enseña la de otra hora
        if (index < 0) {
          show(null);
          return;
        }
        // La hora elegida primero: sus peticiones salen antes que las de la precarga
        const windowFrames = [frames[index], ...frames.slice(Math.max(0, index - 1), index + FRAMES_AHEAD + 1)];
        windowFrames.forEach(ensure);
        // Se enseña al tener sus teselas si sigue siendo la hora del slider o, en un arrastre rápido, si está más cerca de
        // ella que la que se ve: el mapa va pasando por las horas que ya cargaron. Antes cada paso cancelaba la espera del
        // anterior y con el slider en marcha el heatmap no cambiaba hasta soltar. La espera acaba sola: al cargar, al
        // quitarse la fuente o a los 3 s
        whenSourcesLoaded(map, [frameSourceId(current)], () => {
          const pos = (id) => frames.findIndex((f) => f.id === id);
          const goal = pos(target.current);
          if (map.style && created.current.has(current)
            && (current === target.current || Math.abs(index - goal) < Math.abs(pos(shown.current) - goal))) show(current);
        });

        const keep = new Set([...windowFrames.map((f) => f.id), shown.current]);
        for (const id of created.current) {
          if (created.current.size <= FRAMES_MAX) break;
          if (keep.has(id)) continue;
          if (map.getLayer(frameLayerId(id))) map.removeLayer(frameLayerId(id));
          if (map.getSource(frameSourceId(id))) map.removeSource(frameSourceId(id));
          created.current.delete(id);
        }
      } catch (e) {
        console.warn('[frames]', e);
      }
    };
    // Al abrir la página la lista puede llegar antes que el estilo del mapa, y addSource fallaría. Sin esperar a un
    // frame: con el slider en marcha el paso siguiente cancelaba el requestAnimationFrame y el frame no se pedía
    if (map.style._loaded) run(); else map.once('load', run);
    return () => map.off('load', run);
  }, [mapInstance, frames, current, opacity, resampling]);
};

// Hora ISO sin milisegundos ("2026-10-19T17:10:00Z") del VALID_TIMESTAMP / FORECAST_RELEASE del filtro CQL del slider
function cqlTime(filter) {
  const m = filter?.match(/(?:FORECAST_RELEASE|VALID_TIMESTAMP)\s*=\s*'([^']+)'/);
  if (!m) return null;
  let t = m[1].trim().replace(' ', 'T');
  if (!t.endsWith('Z')) t += 'Z';
  return t.split('.')[0].replace('Z', '') + 'Z';
}

// Filtro CQL del run de polígonos: el del slider sin la hora (la elige el filtro de MapLibre). Fuera de CNN, tampoco TOP
// ni CONVECTIVE_MATRIX_RISK
export function runCqlFilter(filter, layer) {
  return (filter || '')
    .split(/\s+AND\s+/i)
    .filter((part) => !part.includes('VALID_TIMESTAMP') && (layer.startsWith('CNN') || !/TOP|CONVECTIVE_MATRIX_RISK/.test(part)))
    .join(' AND ');
}

// Convierte "pred_20250704T010000Z.pmtiles" → "2025-07-04T01:00:00Z"
function filenameToIso(filename) {
  const m = filename?.match(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
}

// Elige el fichero PMTiles para un instante ISO: coincidencia exacta y, si no,
// el frame disponible más cercano (evita congelar el heatmap si el slider no
// cae justo en un frame del modelo, p.ej. turbulence).
export function pickPmtileForTime(files, isoTime) {
  if (!files?.length || !isoTime) return null;
  const clean = isoTime.replace(/[-:]/g, '');
  const exact = files.find(f => f.includes(clean));
  if (exact) return exact;

  const targetMs = Date.parse(isoTime);
  if (!Number.isFinite(targetMs)) return null;
  return files.reduce((best, f) => {
    const ms = Date.parse(filenameToIso(f));
    if (!Number.isFinite(ms)) return best;
    const d = Math.abs(ms - targetMs);
    return !best || d < best.d ? { f, d } : best;
  }, null)?.f ?? null;
}

// Mapa de capa activa → clave del endpoint PMTiles
const PMTILES_LAYER_MAP = {
  'CNN:EU_025_live_demo': 'cnn_025_live',   // nombre de la capa de PMTILES que se leera
  'CNN:EU_01_live_demo': 'cnn_01_live',
  'TURBULENCE:TURBULENCE_0125_live_demo': 'turbulence_live',
  'ICE:ICE_025_live_demo': 'ice_live',
};
const formatDateForDisplay = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  const hours = d.getUTCHours().toString().padStart(2, "0");
  const minutes = d.getUTCMinutes().toString().padStart(2, "0");
  const seconds = d.getUTCSeconds().toString().padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
};
const MapLayers = ({ activeMapBaseLayer, layerOpacity, cqlFilter, onLayerChange, onInitialTimeChange, forecastValue, showGeoJson, setShowGeoJson, showMosaic, setShowMosaic, showFirs, setShowFirs }) => {
  const { current: map } = useMap(); // Moved to top for scope availability
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeLayer, setActiveLayer] = useState('CNN:EU_025_live_demo');
  const [hoverInfo, setHoverInfo] = useState(null);
  const [clickedInfo, setClickedInfo] = useState(null);


  // PMTiles data state
  const [pmtilesConfig, setPmtilesConfig] = useState(null);
  // Fallos que antes dejaban el mapa en blanco (o con los polígonos de otra hora) sin aviso.
  // Retry (retry + 1) vuelve a pedir la lista, monta de nuevo el heatmap y repite el WFS
  const [retry, setRetry] = useState(0);
  const [listFail, setListFail] = useState(false);
  const [pmFail, setPmFail] = useState(null); // { file, notFound }
  const [wfsFail, setWfsFail] = useState(null); // URL del run de polígonos que falló
  const lastInitial = useRef(null);

  // Al cambiar de capa la hora inicial se manda siempre: view-map vuelve a pedir el ISSUE al WFS y, sin ella, ganaba
  // esa fecha (ICE 2026-03-06, turbulence 2026-06-18: sus WFS están parados) y el heatmap se quedaba congelado
  useEffect(() => { lastInitial.current = null; }, [activeLayer]);

  // Fetch PMTiles list cuando cambia la capa activa (si es una capa PMTiles)
  useEffect(() => {
    // Fuera la config de la capa anterior: si no, su heatmap se sigue pintando bajo la nueva capa hasta que llega
    // la nueva lista, o para siempre si la petición falla (p.ej. ice_live sin ficheros todavía)
    setPmtilesConfig(null);
    setListFail(false);
    setPmFail(null); // los ficheros se llaman igual en todas las capas: el fallo de una no vale para otra
    const pmtilesKey = PMTILES_LAYER_MAP[activeLayer];
    if (!pmtilesKey) return;

    let stale = false; // la lista de una capa que ya no está activa no se aplica
    const fetchPmtilesList = async () => {
      try {
        const resp = await axios.get(`${process.env.REACT_APP_API_URL}/api/pmtiles/list?layer=${pmtilesKey}`, { timeout: 10000 });
        if (stale) return;
        setPmtilesConfig(resp.data);
        // El run entero (~50 archivos de 4–100 KB) en segundo plano: luego el slider y el play no esperan a la red
        prefetchPmtiles(resp.data.files.map((file) => `${resp.data.baseUrl}${file}`));

        // Notificar al padre con la hora inicial del fichero PMTiles. En un Retry solo si llegó otro run (los ficheros
        // del anterior ya no están): con el mismo run el slider se queda donde estaba
        const key = `${activeLayer}|${resp.data.initialFile}`;
        const isoDate = filenameToIso(resp.data.initialFile);
        if (isoDate && onInitialTimeChange && key !== lastInitial.current) {
          lastInitial.current = key;
          onInitialTimeChange(isoDate);
        }
      } catch (err) {
        if (stale) return;
        console.error("Error fetching PMTiles list:", err);
        setListFail(true);
      }
    };
    fetchPmtilesList();
    return () => { stale = true; };
  }, [activeLayer, onInitialTimeChange, retry]);

  // Hora del slider. Ni los frames (heatmap y nowcasting) ni los polígonos esperan a un debounce: todo está ya en el
  // navegador y cambiar de hora no pide nada
  const frameTime = useMemo(() => cqlTime(cqlFilter), [cqlFilter]);

  // Fichero PMTiles de la hora del slider: coincidencia exacta y, si no, frame más cercano. Sin esto el heatmap se
  // congela cuando el slider no cae justo en un frame del modelo (turbulence: el slider arranca de ISSUE_TIMESTAMP, no
  // del primer frame PMTiles). Se calcula en el render: con un efecto + estado cada paso pintaba MapLayers dos veces
  // ponytail: nearest-frame; la causa de fondo es el doble origen de forecastValue
  // (ForecastRelease WFS vs initialFile PMTiles). Unificar esos dos si se quiere match exacto.
  const currentPmtileFile = useMemo(() => (pmtilesConfig
    ? pickPmtileForTime(pmtilesConfig.files, frameTime) || pmtilesConfig.initialFile
    : null), [pmtilesConfig, frameTime]);

  // Polígonos WFS del run entero, por hora: { layer, byTime (VALID_TIMESTAMP → FeatureCollection) }
  const [run, setRun] = useState(null);

  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  // Resize y Handlers
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Estables para los controles memoizados (abajo). El padre crea onLayerChange en cada render, o sea en cada paso del
  // slider: se llama siempre al último
  const onLayerChangeRef = useRef(onLayerChange);
  onLayerChangeRef.current = onLayerChange;
  const handleClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleMenuItemClick = useCallback((layerName) => {
    if (layerName !== activeLayer) {
      setActiveLayer(layerName);
      onLayerChangeRef.current(layerName);
      // Immediate clean-up to avoid "ghost" tiles or data
      setHoverInfo(null);
      setClickedInfo(null);
      setShowGeoJson(false);
    }
    setAnchorEl(null);
  }, [activeLayer, setShowGeoJson]);

  const isGeoJsonLayer = [
    'CNN:EU_025_live_demo', 'CNN:EU_01_live_demo', 'CNN:euro_1k_00625_15m',
    'ICE:ICE_025_live_demo', 'TURBULENCE:TURBULENCE_0125_live_demo',
    'DUST:DUST_025_live_demo', 'VISIBILITY:visibility_live_demo'
  ].includes(activeLayer);

  const isCnnLivePmtiles = activeLayer === 'CNN:EU_025_live_demo' || activeLayer === 'CNN:EU_01_live_demo';
  const hasHeatmap = !!PMTILES_LAYER_MAP[activeLayer];
  const isCnnLayer = activeLayer.startsWith('CNN');
  // Turbulence e ICE: polígonos desactivados ("coming soon"), su WFS live está parado (2026-06-18 / 2026-03-06)
  const polygonsComingSoon = activeLayer.startsWith('TURBULENCE') || activeLayer.startsWith('ICE');
  const isNowcasting = activeLayer === 'NOWCASTING:nowcastnet_live_demo';

  // ¿Se van a dibujar polígonos WFS? En capas con heatmap solo si el toggle está activo.
  // Turbulence e ICE tienen los polígonos desactivados → no se pide el WFS, solo los PMTiles.
  const showPolygons = isGeoJsonLayer && (hasHeatmap ? showGeoJson : true);


  // --- 🛰️ FRAMES: heatmap PMTiles (CNN, turbulence, HAIC) y nowcasting (WMS, pasos de 10 min) ---
  // Retry (retry + 1) crea otra secuencia: se vuelven a montar todas las fuentes
  const heatmapFrames = useMemo(() => (hasHeatmap && showMosaic && pmtilesConfig
    ? pmtilesConfig.files.map((file) => ({
      id: `${pmtilesConfig.baseUrl}${file}`,
      source: { type: 'raster', url: `pmtiles://${pmtilesConfig.baseUrl}${file}`, tileSize: 256 },
    }))
    : null), [hasHeatmap, showMosaic, pmtilesConfig, retry]); // eslint-disable-line react-hooks/exhaustive-deps
  const heatmapCurrent = pmtilesConfig && currentPmtileFile ? `${pmtilesConfig.baseUrl}${currentPmtileFile}` : null;

  // Nowcasting: los polígonos de un issue pesan ~100 MB (19 MB en gzip), así que cada paso es un raster WMS que pinta
  // GeoServer. /proxy/wms/nowcasting va directo a AIMethods (~0,1 s por tesela); nowcasting-live probaba antes el
  // GeoServer cloud, que contesta 401 a los ~3 s, y cada tesela pagaba esa espera
  const nowcastFrames = useMemo(() => {
    if (!isNowcasting || !forecastValue) return null;
    const issue = Date.parse(forecastValue);
    // Los 19 pasos del slider de nowcasting: issue + 10 min … issue + 3 h 10 min
    return Array.from({ length: 19 }, (_, step) => {
      const time = new Date(issue + (step + 1) * 600e3).toISOString().replace('.000Z', 'Z');
      const cql = encodeURIComponent(`VALID_TIMESTAMP = '${time}'`);
      return {
        id: `nowcast:${time}`,
        source: {
          type: 'raster', tileSize: 256, bounds: [-11.6, 42.69, 15.85, 63.4],
          tiles: [`${process.env.REACT_APP_API_URL}/proxy/wms/nowcasting?service=WMS&request=GetMap&layers=NOWCASTING:nowcastnet_live_demo&styles=&format=image/vnd.jpeg-png8&transparent=true&version=1.1.1&tiled=true&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}&CQL_FILTER=${cql}`],
        },
      };
    });
  }, [isNowcasting, forecastValue, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  useRasterFrames(map, {
    frames: heatmapFrames || nowcastFrames,
    current: heatmapFrames ? heatmapCurrent : `nowcast:${frameTime}`,
    opacity: layerOpacity || 0.75,
    // ICE son 3 clases (3 colores exactos, teselas solo hasta z2): 'linear' las emborrona e inventa tonos
    // intermedios, igual que en dust. CNN, turbulence y nowcasting son continuos: linear
    resampling: activeLayer.startsWith('ICE') ? 'nearest' : 'linear',
  });

  // --- POLÍGONOS WFS: el run entero en una petición, como los heatmaps precargados ---
  // Las tablas live guardan un solo run: CNN ~200 polígonos (25–40 KB con gzip), dust ~7.000 (400 KB), visibility ~19.000
  // puntos (100 KB), 0,2–1,5 s. El slider solo cambia la hora que recibe MapLibre y moverlo no pide nada; antes cada
  // paso esperaba 200 ms de debounce más la ida y vuelta a GeoServer. La URL solo cambia con la capa o el FL (TOP)
  // ponytail: run entero sin tope; si una capa crece a decenas de MB (nowcasting son ~100 MB), volver a pedirla por hora
  const runUrl = useMemo(() => {
    if (!showPolygons) return null;
    const cql = runCqlFilter(cqlFilter, activeLayer);
    const baseUrl = activeLayer.startsWith('CNN')
      ? `${process.env.REACT_APP_API_URL}/proxy/wfs/cloud`
      : `${process.env.REACT_APP_API_URL}/proxy/wfs`;
    return `${baseUrl}?service=WFS&version=1.0.0&request=GetFeature&typeName=${activeLayer}&outputFormat=application/json${cql ? `&CQL_FILTER=${encodeURIComponent(cql)}` : ''}`;
  }, [activeLayer, cqlFilter, showPolygons]);

  useEffect(() => {
    setWfsFail(null);
    if (!runUrl) return;

    const controller = new AbortController();

    // timeout: sin él una petición colgada no acababa nunca. Solo vale GeoJSON (GeoServer devuelve sus errores como XML)
    axios.get(runUrl, { signal: controller.signal, timeout: 30000 })
      .then(({ data }) => {
        if (!Array.isArray(data?.features)) throw new Error('respuesta sin GeoJSON');
        const sortedFeatures = [...data.features].sort((a, b) => {
          const riskA = a.properties.CONVECTIVE_MATRIX_RISK || a.properties.ICE_MATRIX_RISK || a.properties.TURBULENCE_MATRIX_RISK || a.properties.DUST_MATRIX_RISK || 0;
          const riskB = b.properties.CONVECTIVE_MATRIX_RISK || b.properties.ICE_MATRIX_RISK || b.properties.TURBULENCE_MATRIX_RISK || b.properties.DUST_MATRIX_RISK || 0;
          return riskA - riskB;
        });
        const byTime = new Map();
        sortedFeatures.forEach((f) => {
          const time = f.properties.VALID_TIMESTAMP;
          if (!byTime.has(time)) byTime.set(time, { type: 'FeatureCollection', features: [] });
          byTime.get(time).features.push(f);
        });
        setRun({ layer: activeLayer, byTime });
      })
      .catch(err => {
        if (axios.isCancel(err)) return;
        console.error("WFS Error:", err);
        // Antes se quedaban pintados los polígonos de la hora anterior, como si fueran los de esta
        setRun(null);
        setWfsFail(runUrl);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runUrl, retry]);

  // El run de otra capa no se pinta nunca; el de otro FL sí, hasta que llega el nuevo (no parpadea al mover la altura)
  const runShown = run?.layer === activeLayer ? run : null;
  // MapLibre recibe solo la hora del slider (VALID_TIMESTAMP llega como "2026-10-20T09:00:00Z", el formato de frameTime).
  // Con el run entero y un filtro de hora, cada paso y cada movimiento o zoom del mapa volvían a teselar todo el run
  // (dust ~7.000 polígonos, visibility ~19.000 puntos) y el mapa iba más pesado que pidiendo hora a hora
  const vectorSourceData = isGeoJsonLayer ? runShown?.byTime.get(frameTime) || EMPTY_FC : null;

  // --- ESTILOS ---
  const opacity = layerOpacity || 0.75;

  // --- INTERACCIONES ---
  useEffect(() => {
    if (!map || !isGeoJsonLayer) return;

    // Identificar el ID de la capa activa para interacciones
    let layerId = '';
    if (activeLayer.startsWith('VISIBILITY')) layerId = 'visibility-circle';
    else if (activeLayer.startsWith('CNN')) layerId = 'cnn-fill';
    else if (activeLayer.startsWith('ICE')) layerId = 'ice-fill';
    else if (activeLayer.startsWith('TURBULENCE')) layerId = 'turbulence-fill';
    else if (activeLayer.startsWith('DUST')) layerId = 'dust-fill';

    if (!layerId) return;

    const onMapMouseMove = (e) => {
      if (!map.getLayer(layerId)) return;
      const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
      if (features.length > 0) {
        map.getCanvas().style.cursor = 'pointer';
        setHoverInfo({ lngLat: e.lngLat, feature: features[0], layer: activeLayer });
      } else {
        map.getCanvas().style.cursor = '';
        setHoverInfo(null);
      }
    };

    const onMapClick = (e) => {
      if (!map.getLayer(layerId)) return;
      if (activeLayer.startsWith('VISIBILITY')) {
        const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
        if (features.length > 0) {
          setClickedInfo({ lngLat: e.lngLat, feature: features[0] });
        } else {
          setClickedInfo(null);
        }
      }
    };

    map.on('mousemove', onMapMouseMove);
    map.on('click', onMapClick);
    return () => {
      if (map) {
        map.off('mousemove', onMapMouseMove);
        map.off('click', onMapClick);
        if (map.getCanvas()) map.getCanvas().style.cursor = '';
      }
    };
  }, [map, activeLayer, isGeoJsonLayer]);

  // Heatmap: 404 = ese fichero ya no existe (los *_live/ solo guardan el último run: al llegar uno nuevo se van las horas
  // del anterior y una página abierta de antes las seguía pidiendo); otro error = fallo de carga
  useEffect(() => {
    if (!map || !heatmapCurrent) return;
    const onError = (e) => {
      if (e.sourceId === frameSourceId(heatmapCurrent)) setPmFail({ file: currentPmtileFile, notFound: /\b404\b/.test(e.error?.message) });
    };
    map.on('error', onError);
    return () => map.off('error', onError);
  }, [map, currentPmtileFile, heatmapCurrent]);

  // --- WMS GetFeatureInfo CLICK (CNN live 025) ---
  const [wmsClickLoading, setWmsClickLoading] = useState(false);

  const onWmsClick = useCallback(async (e) => {
    if (!map) return;

    // Usar mapa nativo para project/getBounds/getCanvas
    const nativeMap = map.getMap ? map.getMap() : map;

    const point = nativeMap.project(e.lngLat);
    const bounds = nativeMap.getBounds();
    const canvas = nativeMap.getCanvas();

    // Extraer TIME del cqlFilter (mismo cálculo que getWmsUrl)
    const timeslotMatch = cqlFilter?.match(/(?:FORECAST_RELEASE|VALID_TIMESTAMP)\s*=\s*'([^']+)'/);
    const timeslot = timeslotMatch ? timeslotMatch[1] : '';
    let timeslotIso = '';
    if (timeslot) {
      timeslotIso = timeslot.trim().replace(' ', 'T');
      if (!timeslotIso.endsWith('Z')) timeslotIso += 'Z';
      timeslotIso = timeslotIso.split('.')[0].replace('Z', '') + 'Z';
    }

    const baseUrl = `${process.env.REACT_APP_API_URL}/proxy/wms/cnn-live-info`;
    const bboxStr = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
    const featureInfoUrl = `${baseUrl}?service=WMS&version=1.1.1&request=GetFeatureInfo&layers=CNN:cnn_025_live_demo&query_layers=CNN:cnn_025_live_demo&info_format=application/json&x=${Math.round(point.x)}&y=${Math.round(point.y)}&width=${canvas.width}&height=${canvas.height}&srs=EPSG:4326&bbox=${bboxStr}&TIME=${timeslotIso}`;

    try {
      setWmsClickLoading(true);
      console.log('[GetFeatureInfo] Requesting:', featureInfoUrl);
      const resp = await axios.get(featureInfoUrl);
      console.log('[GetFeatureInfo] Response:', resp.data);
      if (resp.data && resp.data.features && resp.data.features.length > 0) {
        setClickedInfo({ lngLat: e.lngLat, feature: resp.data.features[0], source: 'wms' });
      } else {
        setClickedInfo(null);
      }
    } catch (err) {
      console.error('Error GetFeatureInfo (CNN live):', err);
    } finally {
      setWmsClickLoading(false);
    }
  }, [map, cqlFilter]);

  useEffect(() => {
    if (!map) return;
    const nativeMap = map.getMap ? map.getMap() : map;

    const handleInteraction = () => {
      const zoom = nativeMap.getZoom();
      if (isCnnLivePmtiles && zoom >= 4) {
        nativeMap.getCanvas().style.cursor = 'grab';
        // PMTiles use vector layers, interaction is handled in move/click handlers above
      } else {
        if (isCnnLivePmtiles) {
          nativeMap.getCanvas().style.cursor = '';
        }
      }
    };

    if (isCnnLivePmtiles) {
      handleInteraction();
      nativeMap.on('zoom', handleInteraction);
      return () => {
        nativeMap.off('zoom', handleInteraction);
        if (nativeMap.getCanvas()) nativeMap.getCanvas().style.cursor = '';
      };
    }
  }, [map, isCnnLivePmtiles, onWmsClick]);

  // --- POPUPS ---
  const renderPopup = () => {
    if (!hoverInfo || !hoverInfo.feature) return null;
    const props = hoverInfo.feature.properties;
    const { lng, lat } = hoverInfo.lngLat;
    let content = null;

    if (activeLayer.startsWith('VISIBILITY')) {
      const airport = props.AIRPORT || 'N/A';
      const riskVal = parseInt(props.VISIBILITY_MATRIX_RISK, 10);
      const circleColor = (() => {
        if (riskVal === 4) return 'red';
        if (riskVal === 3) return 'yellow';
        if (riskVal === 2) return 'blue';
        if (riskVal === 1) return 'green';
        return 'gray';
      })();
      const riskText = (() => {
        if (riskVal === 1) return '> 1000m';
        if (riskVal === 2) return '600 - 1000m';
        if (riskVal === 3) return '350 - 600m';
        if (riskVal === 4) return '< 350m';
        return 'N/A';
      })();
      content = (
        <div style={{ fontFamily: 'Arial, sans-serif', padding: '5px', minWidth: '180px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="#000000">
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M21 16v-2l-8-5V4a1 1 0 0 0-2 0v5l-8 5v2l8-2.5V21l-2 1v1h6v-1l-2-1v-7.5z" />
              </svg>
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{airport}</span>
            </div>
            <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: circleColor, border: '1px solid black' }}></div>
          </div>
          <div style={{ fontSize: '14px', marginBottom: '4px', color: '#333' }}>Visibility: <strong>{riskText}</strong></div>
          <div style={{ fontSize: '13px', color: '#007bff', cursor: 'pointer', fontWeight: '500' }}>Click for 48h forecast</div>
        </div>
      );
    }
    else if (activeLayer.startsWith('CNN')) {
      // Support both vector tiles (GRAY_INDEX) and GeoJSON (CONVECTIVE_MATRIX_RISK)
      const riskVal = props.GRAY_INDEX !== undefined ? props.GRAY_INDEX : props.CONVECTIVE_MATRIX_RISK;
      content = ConvectionTooltip({ riskLevel: riskVal, coordinates: formatCoordinates({ lat, lng }) });
    }
    else if (activeLayer.startsWith('ICE')) {
      content = IceTooltip({ riskLevel: props.ICE_MATRIX_RISK, coordinates: formatCoordinates({ lat, lng }) });
    }
    else if (activeLayer.startsWith('TURBULENCE')) {
      content = TurbulenceTooltip({ riskLevel: props.TURBULENCE_MATRIX_RISK, coordinates: formatCoordinates({ lat, lng }) });
    }
    else if (activeLayer.startsWith('DUST')) {
      content = DustTooltip({ riskLevel: props.DUST_MATRIX_RISK, coordinates: formatCoordinates({ lat, lng }) });
    }

    if (!content) return null;
    return <Popup longitude={lng} latitude={lat} closeButton={false} closeOnClick={false} anchor="top" maxWidth="300px" offset={10}>{content}</Popup>;
  };

  const renderClickPopup = () => {
    if (!clickedInfo || !clickedInfo.feature) return null;
    const { lng, lat } = clickedInfo.lngLat;
    const props = clickedInfo.feature.properties;

    if (activeLayer.startsWith('VISIBILITY') && props.AIRPORT) {
      return (
        <Popup longitude={lng} latitude={lat} anchor="bottom" onClose={() => setClickedInfo(null)} closeOnClick={false} maxWidth="500px">
          <div style={{ width: '450px', height: '300px', padding: '10px' }}><VisibilityGraph airport={props.AIRPORT} /></div>
        </Popup>
      );
    }

    // CNN live PMTiles Click popup
    if (isCnnLivePmtiles) {
      const riskVal = props.GRAY_INDEX !== undefined ? props.GRAY_INDEX : props.CONVECTIVE_MATRIX_RISK;
      const grayIndex = parseFloat(riskVal);
      const value = isNaN(grayIndex) ? 'N/A' : grayIndex.toFixed(2);
      const coords = formatCoordinates({ lat, lng });

      return (
        <Popup longitude={lng} latitude={lat} anchor="bottom" onClose={() => setClickedInfo(null)} closeOnClick={false} maxWidth={isCompact ? '280px' : '400px'}>
          <div style={{
            fontFamily: "'Inter', sans-serif",
            padding: isCompact ? '10px' : '14px',
            minWidth: isCompact ? '160px' : '220px',
            borderRadius: '16px',
            fontSize: isCompact ? '12px' : '14px',
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            color: '#1a1a1a',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)'
          }}>
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              color: '#444',
              padding: '6px',
              fontWeight: '800',
              borderRadius: '10px',
              textAlign: 'center',
              marginBottom: isCompact ? '10px' : '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              fontSize: '11px'
            }}>
              Convection Risk
            </div>
            <div style={{ textAlign: 'center', marginBottom: isCompact ? '10px' : '14px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(0,0,0,0.5)', marginBottom: '6px' }}>Risk Index</div>
              <span style={{ fontSize: isCompact ? '24px' : '32px', fontWeight: '900', color: '#1a1a1a', letterSpacing: '-0.03em' }}>{value}</span>
            </div>
            <table style={{ width: '100%', color: '#3152c2', fontSize: '10px', borderCollapse: 'collapse', fontWeight: '600' }}>
              <tbody>
                <tr><td style={{ padding: '2px 6px', opacity: 0.6 }}>LAT</td><td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: '900' }}>{coords.lat}</td></tr>
                <tr><td style={{ padding: '2px 6px', opacity: 0.6 }}>LON</td><td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: '900' }}>{coords.lng}</td></tr>
              </tbody>
            </table>
          </div>
        </Popup>
      );
    }

    return null;
  };

  const isMenuOpen = Boolean(anchorEl);
  const isIconOnly = windowWidth <= 1100;
  const isCompact = useMediaQuery('(max-width:600px)');
  const showText = !isIconOnly && !isCompact;
  const fontSizeInterior = windowWidth <= 500 ? "0.75rem" : "0.85rem";

  // Aviso sobre el mapa: error de carga, hueco de datos o run antiguo. Run = primer fichero en capas con heatmap (lo que
  // se pinta) y el ISSUE del WFS en el resto. Los runs salen cada 6 h y tardan ~2 h: más de 12 h es que no llegan nuevos
  // forecastValue llega null mientras sea el de la capa anterior (view-map)
  const pmFailNow = pmFail?.file === currentPmtileFile ? pmFail : null;
  const runTime = hasHeatmap ? filenameToIso(pmtilesConfig?.initialFile) : forecastValue;
  const runAgeH = runTime ? (Date.now() - Date.parse(runTime)) / 3600e3 : 0;
  // Fallo del WFS solo si es del run actual. Sin polígonos CNN es normal (sin convección); visibility (aeropuertos) y dust
  // sin nada a esa hora = no hay datos. "No data" además solo si la hora es de este run: al cambiar de capa el slider
  // sigue un momento en la hora de la capa anterior (fuera de este run) y salía el aviso ~1 s sin motivo
  const wfsError = !!runUrl && wfsFail === runUrl;
  const sinceRunH = (Date.parse(frameTime) - Date.parse(runTime)) / 3600e3;
  const noPolygons = showPolygons && /^(VISIBILITY|DUST)/.test(activeLayer) && !!runShown && !runShown.byTime.has(frameTime);
  const handleRetry = () => { setPmFail(null); setRetry((n) => n + 1); };
  const notice = (listFail && showMosaic) || (pmFailNow && !pmFailNow.notFound) || wfsError
    ? { kind: 'error', onRetry: handleRetry }
    : pmFailNow ? { kind: 'empty', text: 'A newer run replaced this hour', onRetry: handleRetry }
    : noPolygons && sinceRunH >= 0 && sinceRunH <= 72 ? { kind: 'empty', text: 'No data for this hour', onRetry: handleRetry }
    : runAgeH > 12 ? { kind: 'empty', text: `Latest run: ${runTime.slice(0, 16).replace('T', ' ')} UTC (${runAgeH >= 48 ? `${Math.floor(runAgeH / 24)} days` : `${Math.round(runAgeH)} h`} old)` }
    : {};

  // Controles (menú de capas, siempre montado, y botones HEATMAPS/SECTORS/POLYGONS): no dependen de la hora. Sin memo
  // cada paso del slider los volvía a pintar y eran el 80 % del render de un paso
  const controls = useMemo(() => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Box className='layers-selector'>
        <Button
          onClick={handleClick}
          className={`layers-button ${isIconOnly ? 'icon-only' : ''}`}
          variant="contained"
          startIcon={<LayersIcon />}
          endIcon={showText ? (isMenuOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />) : null}
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            borderRadius: 3.5,
            backgroundColor: 'rgba(35, 155, 105, 0.8)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            color: '#fff',
            minWidth: { xs: 44, md: 140 },
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(35, 155, 105, 0.95)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(35, 155, 105, 0.4)',
              borderColor: 'rgba(255, 255, 255, 0.4)'
            }
          }}
        >
          {showText && (buttonLabels[activeLayer] || 'Select Layer')}
        </Button>
        <Menu 
          id="layers-menu" 
          anchorEl={anchorEl} 
          open={Boolean(anchorEl)} 
          onClose={handleClose} 
          keepMounted 
          MenuListProps={{ dense: true }} 
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{ 
            sx: { 
              backgroundColor: 'rgba(0,0,0,0.75)', 
              color: '#fff', 
              borderRadius: '12px', 
              backdropFilter: 'blur(2px)', 
              transform: 'translateY(-25px) !important',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.72)' 
            } 
          }}
        >
          {[
            'CNN:EU_025_live_demo', 'CNN:EU_01_live_demo', 'CNN:euro_1k_00625_15m',
            'VISIBILITY:visibility_live_demo', 'NOWCASTING:nowcastnet_live_demo',
            'ICE:ICE_025_live_demo', 'TURBULENCE:TURBULENCE_0125_live_demo', 'DUST:DUST_025_live_demo'
          ].map((layerName) => (
            <MenuItem key={layerName} onClick={() => handleMenuItemClick(layerName)} selected={layerName === activeLayer} sx={{ gap: '8px', '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.12)' } }}>
              <ListItemIcon sx={{ minWidth: 32 }}><Radio checked={layerName === activeLayer} sx={{ color: '#bbb', '&.Mui-checked': { color: '#4caf50' } }} /></ListItemIcon>
              <Typography variant="body1" sx={{ color: '#fff', fontSize: fontSizeInterior, fontWeight: layerName === activeLayer ? 700 : 500 }}>{buttonLabels[layerName] || layerName}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: 55,
          left: 5,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 2,
          alignItems: 'center',
          zIndex: 10000,
        }}
      >
        
        {(isCnnLayer || hasHeatmap) && (
          <Badge
            badgeContent={
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                HEATMAPS
              </Typography>
            }
            color="error"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiBadge-badge': {
                padding: '0 4px',
                height: '16px',
                minWidth: '30px',
                top: '0px',
                right: '-7px'
              },
            }}
          >
            <IconButton
              onClick={() => setShowMosaic(!showMosaic)}
              sx={{
                bgcolor: showMosaic ? '#38a477' : '#ffffff',
                color: showMosaic ? '#ffffff' : '#38a477',
                '&:hover': { bgcolor: showMosaic ? '#38a477' : '#f5f5f5' },
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                width: { xs: 38, md: 42 },
                height: { xs: 38, md: 42 },
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(16px)',
                pointerEvents: 'auto',
                marginBottom: '18px',
                marginLeft: '7.5px',
                borderRadius: 10
              }}
              title="Toggle Heatmap Layer"
            >
              <LayersIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
            </IconButton>
          </Badge>
        )}

        {/* FIRs/UIRs: se ven en todas las capas. Sin HEATMAPS/POLYGONS ocupa su hueco (mismo marginBottom) */}
        <Badge
          badgeContent={
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
              SECTORS
            </Typography>
          }
          color="error"
          overlap="circular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiBadge-badge': {
              padding: '0 4px',
              height: '16px',
              minWidth: '30px',
              top: '0px',
              right: '-7px'
            },
          }}
        >
          <IconButton
            onClick={() => setShowFirs(!showFirs)}
            aria-pressed={showFirs}
            sx={{
              bgcolor: showFirs ? '#38a477' : '#ffffff',
              color: showFirs ? '#ffffff' : '#38a477',
              '&:hover': { bgcolor: showFirs ? '#38a477' : '#f5f5f5' },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              width: { xs: 38, md: 42 },
              height: { xs: 38, md: 42 },
              border: '1px solid rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(16px)',
              pointerEvents: 'auto',
              marginBottom: (isCnnLayer || hasHeatmap) ? '2.5px' : '18px',
              marginLeft: '7.5px',
              borderRadius: 10
            }}
            title="Toggle FIR/UIR sectors"
          >
            <Grid4x4Icon sx={{ fontSize: { xs: 18, md: 22 } }} />
          </IconButton>
        </Badge>

        {(isCnnLayer || hasHeatmap) && (
          <Badge
            badgeContent={
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                POLYGONS
              </Typography>
            }
            color="error"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiBadge-badge': {
                padding: '0 4px',
                height: '16px',
                minWidth: '30px',
                top: '0px',
                right: '-7px'
              },
            }}
          >
            <IconButton
              onClick={() => setShowGeoJson(!showGeoJson)}
              disabled={polygonsComingSoon}
              sx={{
                bgcolor: showGeoJson ? '#38a477' : '#ffffff',
                color: showGeoJson ? '#ffffff' : '#38a477',
                '&:hover': { bgcolor: showGeoJson ? '#38a477' : '#f5f5f5' },
                '&.Mui-disabled': { bgcolor: '#ffffff', color: '#38a477', opacity: 0.4 },
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                width: { xs: 38, md: 42 },
                height: { xs: 38, md: 42 },
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(16px)',
                pointerEvents: 'auto',
                marginBottom: '2.5px',
                marginLeft: '7.5px',
                borderRadius: 10
              }}
              title={polygonsComingSoon ? 'Polygons (coming soon)' : 'Toggle Polygons Layer'}
            >
              <MapIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
            </IconButton>
          </Badge>
        )}
      </Box>
    </Box>
  ), [activeLayer, anchorEl, isMenuOpen, isIconOnly, showText, fontSizeInterior, showMosaic, setShowMosaic, showFirs, setShowFirs,
    showGeoJson, setShowGeoJson, polygonsComingSoon, isCnnLayer, hasHeatmap, handleClick, handleClose, handleMenuItemClick]);

  return (
    <>


      {controls}

      {/* HEATMAPS PMTILES y NOWCASTING: los gestiona useRasterFrames (una fuente por hora) */}

      {/* GEOJSON LAYERS: la hora del slider, sacada del run en memoria */}
      {showPolygons && vectorSourceData && (
        <Source type="geojson" data={vectorSourceData}>
          {activeLayer.startsWith('VISIBILITY') && (
            <Layer id="visibility-circle" type="circle" paint={{
              'circle-radius': 4.35,
              'circle-stroke-color': '#000000',
              'circle-stroke-width': 1.25,
              'circle-color': ['match', ['to-number', ['get', 'VISIBILITY_MATRIX_RISK']], 4, 'red', 3, 'yellow', 2, 'blue', 1, 'green', 'gray'],
              'circle-opacity': 1
            }} />
          )}
          {activeLayer.startsWith('CNN') && (
            <Layer id="cnn-fill" type="fill" paint={{
              'fill-color': [
                'match',
                ['get', 'CONVECTIVE_MATRIX_RISK'],
                1, '#ffff00', // 1: Low
                2, '#FFA500', // 2: Medium
                3, '#DC143C', // 3: High
                'transparent' // Fallback para cualquier otro valor
              ],
              'fill-opacity': opacity,
              'fill-outline-color': '#a3a1a1c0'
              // 'fill-outline-color': 'transparent'
            }} />
          )}
          {activeLayer.startsWith('ICE') && (
            <Layer id="ice-fill" type="fill" paint={{
              'fill-color': ['match', ['get', 'ICE_MATRIX_RISK'], 1, '#7fd4f9', 2, '#1664c0', 3, '#311c93', 'transparent'],
              'fill-opacity': opacity
            }} />
          )}
          {activeLayer.startsWith('TURBULENCE') && (
            <Layer id="turbulence-fill" type="fill" paint={{
              'fill-color': ['match', ['get', 'TURBULENCE_MATRIX_RISK'], 1, '#ffd700', 2, '#ff0000', 3, '#ff0000', 'transparent'],
              'fill-opacity': opacity
            }} />
          )}
          {activeLayer.startsWith('DUST') && (
            <Layer id="dust-fill" type="fill" paint={{
              'fill-color': ['match', ['get', 'DUST_MATRIX_RISK'], 1, '#d2bfa8', 2, '#967157', 3, '#3f2b21', 'transparent'],
              'fill-opacity': opacity
            }} />
          )}
        </Source>
      )}

      {renderPopup()}
      {renderClickPopup()}

      {/* Portal al contenedor del mapa: este componente vive dentro de la cajita del botón de capas (abajo a la izquierda)
          y ahí el aviso quedaba estrujado. Por encima de la barra del slider (80 px en escritorio, más alta en móvil) */}
      {map && createPortal(
        <MapNotice kind={notice.kind} empty={notice.text} onRetry={notice.onRetry} bottom={{ xs: 200, md: 110 }} />,
        map.getContainer()
      )}
    </>
  );
};

export default MapLayers;
